import { IResult } from '../models/HttpResult';
import {
  ContractParametrizationStatus,
  ContractReview,
  ContractReviewApplyResult,
  ContractReviewFinding,
  FindingDecisionItem,
  NewContractReview,
  UpdateFindingDecision as UpdateFindingDecisionCommand
} from '../models/Entities/Contracts/ContractReview';
import { apiRequest, buildHeaders, buildURL, handleApiError } from './commons/CommonService.Functions';

// Revisión de contratos con IA (spec 012): PractitionerContracts.API 019 vía gateway (contracts/contract-review-api.md).
const BaseURL = (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '') + '/contracts';

/** Tamaño de la página del historial: las últimas 50 revisiones (contrato §2). */
export const REVIEW_HISTORY_PAGE_SIZE = 50;

/**
 * Como `apiRequest`, pero tolera un cuerpo vacío (devuelve `undefined`). Contracts siempre responde con cuerpo
 * (DEC-001); queda como tolerancia para que un 202/204 sin cuerpo no rompa el `response.json()`.
 */
export const requestAllowEmpty = async <T>(
  url: string,
  token: string,
  context: string,
  options: RequestInit = {}
): Promise<T | undefined> => {
  try {
    const response = await fetch(url, { ...options, headers: buildHeaders(token, options.headers as Record<string, string>) });
    if (!response.ok) throw response;
    const text = await response.text();
    if (!text || !text.trim()) return undefined;
    const body: IResult<T> = JSON.parse(text);
    return body.data;
  } catch (error) {
    throw await handleApiError(error, context);
  }
};

// POST contracts/{ContractId}/reviews → 202 Response<ContractReview>
export const CreateContractReview = async (
  contractId: number,
  command: NewContractReview,
  token: string
): Promise<ContractReview | undefined> =>
  requestAllowEmpty<ContractReview>(buildURL(BaseURL, contractId, 'reviews'), token, 'CreateContractReview', {
    method: 'POST',
    body: JSON.stringify(command)
  });

// GET contracts/{ContractId}/reviews?PageNumber=1&PageSize=50 → PagedResponse<ContractReview[]> (404 → [])
export const GetContractReviews = async (contractId: number, token: string): Promise<ContractReview[]> => {
  const url = `${buildURL(BaseURL, contractId, 'reviews')}?PageNumber=1&PageSize=${REVIEW_HISTORY_PAGE_SIZE}`;
  try {
    const response = await fetch(url, { headers: buildHeaders(token) });
    if (response.status === 404) return [];
    if (!response.ok) throw response;
    const body: IResult<ContractReview[]> = await response.json();
    return body.data ?? [];
  } catch (error) {
    throw await handleApiError(error, 'GetContractReviews');
  }
};

// GET contracts/reviews/{ReviewId} → Response<ContractReview>
export const GetContractReviewById = async (reviewId: string, token: string): Promise<ContractReview> =>
  apiRequest<ContractReview>(buildURL(BaseURL, 'reviews', reviewId), token, 'GetContractReviewById');

// PATCH contracts/reviews/findings/{FindingId} → Response<ContractReviewFinding>
export const UpdateFindingDecision = async (
  findingId: number,
  body: UpdateFindingDecisionCommand,
  token: string
): Promise<ContractReviewFinding | undefined> =>
  requestAllowEmpty<ContractReviewFinding>(buildURL(BaseURL, 'reviews', 'findings', findingId), token, 'UpdateFindingDecision', {
    method: 'PATCH',
    body: JSON.stringify(body)
  });

// PATCH contracts/reviews/{ReviewId}/findings (DEC-004): 1..200, todo o nada → Response<ContractReviewFinding[]>
export const UpdateFindingDecisionBulk = async (
  reviewId: string,
  items: FindingDecisionItem[],
  token: string
): Promise<ContractReviewFinding[]> =>
  (await requestAllowEmpty<ContractReviewFinding[]>(buildURL(BaseURL, 'reviews', reviewId, 'findings'), token, 'UpdateFindingDecisionBulk', {
    method: 'PATCH',
    body: JSON.stringify(items)
  })) ?? [];

// POST contracts/reviews/{ReviewId}/apply (sin cuerpo) → Response<ContractReviewApplyResult>
export const ApplyContractReview = async (reviewId: string, token: string): Promise<ContractReviewApplyResult | undefined> =>
  requestAllowEmpty<ContractReviewApplyResult>(buildURL(BaseURL, 'reviews', reviewId, 'apply'), token, 'ApplyContractReview', {
    method: 'POST'
  });

// GET contracts/{ContractId}/parametrization-status (DEC-003; Contracts 021 §2: + hasSettlements, canImport, importBlockedReason)
export const GetContractParametrizationStatus = async (
  contractId: number,
  token: string
): Promise<ContractParametrizationStatus> =>
  apiRequest<ContractParametrizationStatus>(
    buildURL(BaseURL, contractId, 'parametrization-status'),
    token,
    'GetContractParametrizationStatus'
  );

// POST contracts/reviews/findings/{FindingId}/undo (Contracts 021 §4.1) → Response<ContractReviewFinding>
export const UndoFinding = async (findingId: number, token: string): Promise<ContractReviewFinding | undefined> =>
  requestAllowEmpty<ContractReviewFinding>(buildURL(BaseURL, 'reviews', 'findings', findingId, 'undo'), token, 'UndoFinding', {
    method: 'POST'
  });

// POST contracts/reviews/{ReviewId}/undo (Contracts 021 §4.2) → Response<ContractReviewApplyResult>, la forma de apply
export const UndoContractReview = async (reviewId: string, token: string): Promise<ContractReviewApplyResult | undefined> =>
  requestAllowEmpty<ContractReviewApplyResult>(buildURL(BaseURL, 'reviews', reviewId, 'undo'), token, 'UndoContractReview', {
    method: 'POST'
  });
